import React, { FC, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import { ClinicFormData } from 'models/forms';
import ClinicForm from 'components/shared/clinic-form/clinic-form.component';
import { Clinic } from 'models/clinic';
import { modifyClinicTemplate } from 'apollo/templates/clinic';

type EditClinicDialogProps = {
    dialogOpen: boolean;
    clinic: Clinic | undefined;
    onCloseDialog: () => void;
    onClinicModified: () => void;
    allowFullEdit?: boolean;
};
const EditClinicDialog: FC<EditClinicDialogProps> = ({
    dialogOpen,
    clinic,
    onCloseDialog,
    onClinicModified,
    allowFullEdit,
}: EditClinicDialogProps) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [currentClinic, setCurrentClinic] = useState<Clinic>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);

    const [modifyClinic] = useMutation(modifyClinicTemplate, {
        onCompleted: ({}) => {
            onClinicModified();
            setErrorMessage(undefined);
            setIsLoading(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setCurrentClinic(clinic);
    }, [clinic]);

    const handleCloseDialog = () => {
        setErrorMessage(undefined);
        setIsLoading(false);
        onCloseDialog();
    };

    const handleFormSubmitted = (formData: ClinicFormData) => {
        setIsLoading(true);
        modifyClinic({
            variables: {
                clinicId: currentClinic?._id,
                clinic: {
                    name: formData.name,
                    officialName: formData.officialName,
                    organizationNumber: formData.organizationNumber,
                    gln: formData.gln,
                    contact: formData.contact,
                    invoicing: formData.invoicing,
                    isNotPickUpAndDeliveryPlace: formData.isNotPickUpAndDeliveryPlace,
                    externalInfo: formData.externalInfo,
                    active: formData.active,
                    isWhiteLabeled: formData.isWhiteLabeled,
                    ptjCustomerNumber: formData.ptjCustomerNumber,
                    crediFlow: {
                        partyID: formData.crediflowPartyId,
                    },
                    localeFormat: formData.localeFormat,
                },
            },
        });
    };

    return (
        <Dialog fullWidth maxWidth="md" open={dialogOpen} onClose={handleCloseDialog}>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1}>{translate('organization:UPDATE_CLINIC')}</Box>
                    <Box>
                        <Typography color="gray" variant="caption">
                            {currentClinic?._id}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
                {!isLoading && currentClinic ? (
                    <ClinicForm
                        clinic={currentClinic}
                        onClose={handleCloseDialog}
                        onFormSubmitted={handleFormSubmitted}
                        submitButtonText={translate('organization:UPDATE_CLINIC')}
                        allowFullEdit={allowFullEdit}
                    />
                ) : (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EditClinicDialog;
